import { useState } from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';
import { updateSalesTimeline } from '../../api/salesApi';

const PP = "'Poppins', sans-serif";

// ISO datetime -> 'YYYY-MM-DD' for <input type=date>; '' when empty.
const toDateInput = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    // local date parts so the picker shows the stored day, not a TZ-shifted one
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};
// 'YYYY-MM-DD' -> ISO datetime (noon, to dodge TZ roll-over) or null.
const toIso = (s) => (s ? new Date(`${s}T12:00:00`).toISOString() : null);
const fmt = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Stage order. `edit` = admin/group-admin sets it manually; the two auto stages
// (Payment 1 = deposit date, Completed = on completion) are read-only here.
const STAGES = [
    { key: 'depositOn', label: 'Deposit', edit: false, note: 'deposit date' },
    { key: 'sedaSubmittedOn', label: 'SEDA Docs Submitted', edit: true },
    { key: 'sedaApprovedOn', label: 'SEDA Approval', edit: true },
    { key: 'siteVisitOn', label: 'Site Visit', edit: true },
    { key: 'payment', label: 'Payment', edit: false, payment: true },
    { key: 'installationOn', label: 'Installation', edit: true },
    { key: 'tnbApprovedOn', label: 'TNB Approval', edit: true },
    { key: 'completedOn', label: 'Completed', edit: false, note: 'on completion' },
];
const EDIT_KEYS = STAGES.filter(s => s.edit).map(s => s.key);

export default function ProjectTimeline({ sale, canEdit }) {
    const [dates, setDates] = useState(() =>
        EDIT_KEYS.reduce((acc, k) => ({ ...acc, [k]: toDateInput(sale?.[k]) }), {}));
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);

    const setField = (k, v) => { setDates(p => ({ ...p, [k]: v })); setSaved(false); };

    // Payment progress (from the sale's payment-doc totals) drives the Payment node.
    const paid = Number(sale?.paidTotal) || 0;
    const total = Number(sale?.totalPrice) || 0;
    const payPct = total > 0 ? Math.round((paid / total) * 100) : 0;
    const fullyPaid = !!sale?.isFullyPaid || (total > 0 && paid >= total);

    // Done-state value: payment node = fully paid; manual stages read local input;
    // other auto stages read the sale.
    const valueOf = (s) => (s.payment ? fullyPaid : s.edit ? dates[s.key] : sale?.[s.key]);

    const save = async () => {
        setBusy(true);
        try {
            await updateSalesTimeline({
                salesId: sale.salesId,
                sedaSubmittedOn: toIso(dates.sedaSubmittedOn),
                sedaApprovedOn: toIso(dates.sedaApprovedOn),
                siteVisitOn: toIso(dates.siteVisitOn),
                installationOn: toIso(dates.installationOn),
                tnbApprovedOn: toIso(dates.tnbApprovedOn),
            });
            setSaved(true);
        } catch {
            alert('Failed to save the timeline. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    // The "current" stage = the one right AFTER the furthest-completed stage, so the
    // blink tracks real progress even when later stages were dated before earlier ones
    // (e.g. Installation filled while SEDA is still blank). Only this node blinks; the
    // other still-empty stages stay plain. -1 when everything is done (no blink).
    let lastDoneIdx = -1;
    STAGES.forEach((s, i) => { if (valueOf(s)) lastDoneIdx = i; });
    const currentIdx = (lastDoneIdx + 1 < STAGES.length) ? lastDoneIdx + 1 : -1;

    return (
        <Box>
            <style>{`@keyframes tlPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(26,140,255,0.55); } 50% { box-shadow: 0 0 0 5px rgba(26,140,255,0); } }`}</style>
            <Typography sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', fontSize: '0.9rem', mb: 1.5 }}>
                Project Timeline
            </Typography>
            <Box>
                {STAGES.map((s, i) => {
                    const done = !!valueOf(s);
                    const isCurrent = i === currentIdx;
                    const last = i === STAGES.length - 1;
                    return (
                        <Box key={s.key} sx={{ display: 'flex', gap: 1.25 }}>
                            {/* node + connector line */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Box sx={{
                                    width: 14, height: 14, borderRadius: '50%', flexShrink: 0, mt: '3px',
                                    bgcolor: done ? '#16A34A' : isCurrent ? '#1A8CFF' : '#fff',
                                    border: `2px solid ${done ? '#16A34A' : isCurrent ? '#1A8CFF' : '#CBD5E1'}`,
                                    animation: isCurrent ? 'tlPulse 1.4s ease-in-out infinite' : 'none',
                                }} />
                                {!last && <Box sx={{ width: 2, flex: 1, minHeight: s.edit && canEdit ? 28 : 16, bgcolor: done ? '#86EFAC' : '#E2E8F0' }} />}
                            </Box>
                            {/* label + date / input */}
                            <Box sx={{ pb: 1.5, flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: isCurrent ? 700 : 600, color: done ? '#0F172A' : isCurrent ? '#1A8CFF' : '#64748B' }}>
                                    {s.label}
                                    {!s.edit && s.note && (
                                        <Typography component="span" sx={{ fontSize: '0.62rem', color: '#94A3B8', ml: 0.5, fontWeight: 400 }}>
                                            · auto ({s.note})
                                        </Typography>
                                    )}
                                    {s.payment && (
                                        <Typography component="span" sx={{ fontSize: '0.62rem', color: '#94A3B8', ml: 0.5, fontWeight: 400 }}>
                                            · from payment docs
                                        </Typography>
                                    )}
                                </Typography>
                                {s.payment ? (
                                    <Box sx={{ mt: 0.5 }}>
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', fontWeight: 600, color: fullyPaid ? '#16A34A' : '#475569' }}>
                                            RM {paid.toLocaleString()} / RM {total.toLocaleString()} · {payPct}%
                                        </Typography>
                                        <Box sx={{ height: 5, mt: 0.4, borderRadius: 3, bgcolor: '#F1F5F9', overflow: 'hidden' }}>
                                            <Box sx={{ height: '100%', width: `${Math.min(100, payPct)}%`, bgcolor: fullyPaid ? '#16A34A' : '#1A8CFF', borderRadius: 3, transition: 'width 0.6s ease' }} />
                                        </Box>
                                    </Box>
                                ) : s.edit && canEdit ? (
                                    <TextField
                                        type="date" size="small" value={dates[s.key] || ''}
                                        onChange={(e) => setField(s.key, e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ mt: 0.5, '& .MuiInputBase-root': { fontFamily: PP, fontSize: '0.76rem', borderRadius: '8px' } }}
                                    />
                                ) : (
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.76rem', color: done ? '#16A34A' : '#94A3B8', mt: 0.25 }}>
                                        {fmt(valueOf(s))}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            {canEdit && (
                <Button
                    size="small" variant="contained" disabled={busy} onClick={save}
                    sx={{ mt: 0.5, fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px', boxShadow: 'none' }}
                >
                    {busy ? 'Saving…' : saved ? 'Saved ✓' : 'Save Timeline'}
                </Button>
            )}
        </Box>
    );
}
